import type { QueryClient } from '@tanstack/react-query'

import { GenerationStatus } from '../shared/enums/GenerationStatus'
import type { ImageStyleGeneration } from '../shared/models/AIGeneration'
import { queueKeys } from '../shared/queries/useQueue'
import { shotsKeys } from '../shared/queries/useShots'
import { storyboardsKeys } from '../shared/queries/useStoryboards'
import { showToast } from '../utils/fluentToast'

function isGenerationStatus(value: unknown): value is GenerationStatus {
  return typeof value === 'number' && new Set<unknown>(Object.values(GenerationStatus)).has(value)
}

export function handleImageStyleNotification(
  queryClient: QueryClient,
  message: string,
): ImageStyleGeneration {
  const result: unknown = JSON.parse(message)
  if (
    typeof result !== 'object' ||
    result === null ||
    !('CommercialId' in result) ||
    typeof result.CommercialId !== 'string' ||
    !('ShotId' in result) ||
    typeof result.ShotId !== 'string' ||
    !('VariantId' in result) ||
    typeof result.VariantId !== 'string' ||
    !('SourceImageId' in result) ||
    typeof result.SourceImageId !== 'string' ||
    !('ImageStylePromptId' in result) ||
    typeof result.ImageStylePromptId !== 'string' ||
    !('ActivityId' in result) ||
    typeof result.ActivityId !== 'string' ||
    !('Status' in result) ||
    !isGenerationStatus(result.Status)
  ) {
    throw new Error('Invalid image style notification')
  }
  void queryClient.invalidateQueries({
    queryKey: shotsKeys.variant(result.ShotId, result.VariantId),
    exact: true,
  })
  void queryClient.invalidateQueries({
    queryKey: shotsKeys.variant(result.ShotId),
    exact: true,
  })
  void queryClient.invalidateQueries({ queryKey: queueKeys.all })
  void queryClient.invalidateQueries({
    queryKey: storyboardsKeys.detail(result.CommercialId),
  })
  void queryClient.invalidateQueries({
    queryKey: storyboardsKeys.styleStatus(result.CommercialId, result.ActivityId),
  })

  if (result.Status === GenerationStatus.Completed) {
    showToast(
      `Image style completed. Review the shot images (activity ${result.ActivityId}).`,
      'success',
    )
  } else if (
    result.Status === GenerationStatus.Failed ||
    result.Status === GenerationStatus.CompletedWithErrors ||
    result.Status === GenerationStatus.Cancelled
  ) {
    showToast(
      `Image style failed for image ${result.SourceImageId}, style ${result.ImageStylePromptId} (activity ${result.ActivityId}). Check activities for details.`,
      'error',
    )
  }
  return {
    CommercialId: result.CommercialId,
    ShotId: result.ShotId,
    VariantId: result.VariantId,
    SourceImageId: result.SourceImageId,
    ImageStylePromptId: result.ImageStylePromptId,
    ActivityId: result.ActivityId,
    Status: result.Status,
  }
}
